import { FC, memo, useEffect, useMemo, useRef, useState } from 'react'
import { useColumnCount } from '@/client/hooks/useMasonryColumns'
import { distributeImages } from '@/client/utils/distributeImage'
import MasonryColumn from './MasonryColumn'
import * as S from './styles'

const MasonryGridComponent: FC<any> = ({ images = [], gap = 16 }) => {
  // Column count for masonry.
  const columnCount = useColumnCount()
  // Total Images in each batch.
  const BATCH_SIZE = 10

  const [visibleCount, setVisibleCount] = useState(BATCH_SIZE)

  const loadMoreRef = useRef<HTMLDivElement | null>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const isLoadingRef = useRef(false)

  useEffect(() => {
    setVisibleCount(BATCH_SIZE)
  }, [images, columnCount])
  const visibleImages = useMemo(() => images.slice(0, visibleCount), [images, visibleCount, columnCount])

  const columnSet = useMemo(() => distributeImages(visibleImages, columnCount), [visibleImages, columnCount])

  useEffect(() => {
    if (!loadMoreRef.current) return

    if (observerRef.current) observerRef.current.disconnect()

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return
        if (isLoadingRef.current) return

        isLoadingRef.current = true

        setVisibleCount((prev) => Math.min(prev + BATCH_SIZE, images.length))

        setTimeout(() => {
          isLoadingRef.current = false
        }, 150)
      },
      {
        rootMargin: '250px'
      }
    )

    observerRef.current.observe(loadMoreRef.current)

    return () => observerRef.current?.disconnect()
  }, [images.length, BATCH_SIZE])

  return (
    <>
      <S.Masonry gap={gap}>
        {Array.from({ length: columnCount }).map((_, index) => (
          <MasonryColumn
            key={index}
            images={visibleImages}
            columnSet={columnSet[index]}
            isLastColumn={index === columnCount - 1}
            gap={gap}
          />
        ))}
      </S.Masonry>

      <div ref={loadMoreRef} />
    </>
  )
}

export default memo(MasonryGridComponent)
